import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Scissors, Wand2 } from "lucide-react";
import { api } from "@/lib/api";

export const TruncatedBlocksPanel = ({ onCount }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const r = await api.get("/admin/consulting/truncated-blocks");
      setItems(r.data.items || []);
      onCount?.(r.data.count || 0);
    } catch { toast.error("No pudimos cargar los bloques cortados"); }
    finally { setLoading(false); }
  }

  async function repair(it) {
    if (!window.confirm(`¿Completar con Claude los bloques cortados de "${it.company_name}"?\n\nSe continúa el texto desde donde se cortó (no se regenera el bloque). Los bloques editados manualmente no se tocan.`)) return;
    try {
      await api.post(`/admin/consulting/${it.id}/repair-truncated`);
      toast.success("Reparación lanzada. Refrescando en 60 s.");
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, repair_status: "running" } : x)));
      setTimeout(refresh, 60_000);
    } catch (err) { toast.error(err?.response?.data?.detail || "No pudimos lanzar la reparación"); }
  }

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="admin-truncated-panel">
      <div className="rounded-2xl bg-amber-50/60 border border-amber-200 p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-600 text-white flex items-center justify-center flex-shrink-0">
          <Scissors className="w-5 h-5" />
        </div>
        <p className="flex-1 text-sm text-slate-700 leading-relaxed">
          Informes listos con bloques que terminan a media frase (generados antes de la protección anti-corte). «Reparar» pide a Claude que continúe exactamente donde se cortó.
        </p>
        <button onClick={refresh} className="btn-ghost flex-shrink-0" data-testid="admin-truncated-refresh-btn" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />Refrescar
        </button>
      </div>
      {items.length === 0 ? (
        <div className="card text-center text-gray-500 py-10" data-testid="admin-truncated-empty">
          {loading ? "Analizando informes…" : "Ningún informe con bloques cortados."}
        </div>
      ) : items.map((it) => (
        <TruncatedRow key={it.id} it={it} onRepair={() => repair(it)} />
      ))}
    </div>
  );
};

const TruncatedRow = ({ it, onRepair }) => {
  const running = it.repair_status === "running";
  const repairable = it.blocks.some((b) => !b.edited);
  return (
    <div className="rounded-2xl bg-white border border-amber-100 p-5" data-testid={`admin-truncated-row-${it.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate">{it.company_name}</div>
          <div className="text-xs text-slate-500 truncate">{it.user_email}</div>
          {it.repair_status === "failed" && <div className="text-xs text-red-600 mt-1">Último intento falló: {it.repair_error}</div>}
        </div>
        <button onClick={onRepair} disabled={running || !repairable} className="btn-primary flex-shrink-0 disabled:opacity-50" data-testid={`admin-truncated-repair-btn-${it.id}`}>
          <Wand2 className={`w-4 h-4 ${running ? "animate-pulse" : ""}`} />{running ? "Reparando…" : "Reparar"}
        </button>
      </div>
      <ul className="space-y-1.5">
        {it.blocks.map((b) => (
          <li key={b.n} className="text-xs text-slate-600" data-testid={`admin-truncated-block-${it.id}-${b.n}`}>
            <span className="font-semibold text-slate-800">Bloque {b.n}{b.edited ? " (editado a mano, revisar manualmente)" : ""}:</span>{" "}
            <span className="font-mono">…{b.tail}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
